import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Alert,
  Center,
  Box,
  Loader,
  Badge,
  Card,
  PasswordInput,
  Modal,
  Image,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconX,
  IconMail,
  IconLock,
} from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { useInviteByToken, useAcceptInvite } from '@/hooks/api/useOrganizationMembers';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { useOrgStore } from '@/stores/orgStore';
import { notifications } from '@mantine/notifications';
import { useQuery } from '@tanstack/react-query';

export function AcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const { user, login } = useAuth();

  const { data: invite, isLoading, error } = useInviteByToken(token);
  const acceptInvite = useAcceptInvite();
  const [declined, setDeclined] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);

  // Fetch custom role name if invite has a custom role
  const { data: customRole } = useQuery({
    queryKey: ['custom-role', invite?.custom_role_id],
    queryFn: async () => {
      if (!invite?.custom_role_id) return null;
      const { data, error } = await supabase
        .from('custom_roles')
        .select('name')
        .eq('id', invite.custom_role_id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!invite?.custom_role_id,
  });

  const passwordForm = useForm({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validate: {
      password: (value) => {
        if (value.length < 6) return 'Password must be at least 6 characters';
        return null;
      },
      confirmPassword: (value, values) => {
        if (value !== values.password) return 'Passwords do not match';
        return null;
      },
    },
  });

  const handleAccept = async () => {
    if (!token || !invite) return;

    // Check if user exists
    if (!user) {
      // Check if an account exists with this email
      const { data: existingUser } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', invite.email.toLowerCase())
        .single();

      if (existingUser) {
        // User exists but not logged in
        setNeedsLogin(true);
        return;
      }

      // No user account exists - show password creation modal
      setShowPasswordModal(true);
      return;
    }

    // User is logged in - accept invite
    try {
      await acceptInvite.mutateAsync(token);

      // Fetch and set the organization in the store
      const { data: membership } = await supabase
        .from('organization_members')
        .select('*, organization:organizations(*)')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .single();

      if (membership?.organization) {
        useOrgStore.getState().setOrganization(membership.organization, membership);
      }

      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      console.error('Failed to accept invite:', err);
    }
  };

  const handleCreateAccountAndAccept = async (values: typeof passwordForm.values) => {
    if (!invite || !token) return;

    setIsCreatingAccount(true);

    try {
      // Create account with Supabase Auth
      // Pass metadata to skip auto-creating an organization (user is joining via invite)
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: invite.email,
        password: values.password,
        options: {
          data: {
            skip_org_creation: true, // Flag for the database trigger
          },
        },
      });

      if (signUpError) throw signUpError;

      if (!authData.user) {
        throw new Error('Failed to create account');
      }

      // Sign in with the new account
      await login(invite.email, values.password);

      // Accept the invite
      await acceptInvite.mutateAsync(token);

      // Fetch and set the organization in the store
      const { data: membership } = await supabase
        .from('organization_members')
        .select('*, organization:organizations(*)')
        .eq('user_id', authData.user.id)
        .eq('status', 'active')
        .single();

      if (membership?.organization) {
        useOrgStore.getState().setOrganization(membership.organization, membership);
      }

      notifications.show({
        title: 'Account created',
        message: 'Your account has been created and you have joined the organization',
        color: 'green',
      });

      setTimeout(() => navigate('/'), 1500);
    } catch (err: any) {
      notifications.show({
        title: 'Error',
        message: err.message || 'Failed to create account',
        color: 'red',
      });
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const handleDecline = () => {
    setDeclined(true);
    setTimeout(() => navigate(user ? '/dashboard' : '/login'), 2000);
  };

  // Show loading state
  if (isLoading) {
    return (
      <Box
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0b487c 0%, #1a6eb5 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
        }}
      >
        <Paper withBorder shadow="xl" p="xl" radius="md" style={{ maxWidth: 500 }}>
          <Center>
            <Stack align="center" gap="md">
              <Loader size="lg" />
              <Text>Loading invitation details...</Text>
            </Stack>
          </Center>
        </Paper>
      </Box>
    );
  }

  // Show error state (invalid/expired invite)
  if (error || !invite) {
    return (
      <Box
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0b487c 0%, #1a6eb5 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
        }}
      >
        <Container size={500}>
          <Center mb="xl">
            <Image src="/logo-inverse.png" alt="TrustPuma" h={80} w="auto" />
          </Center>

          <Paper withBorder shadow="xl" p="xl" radius="md">
            <Alert icon={<IconAlertCircle size={24} />} color="red" title="Invalid Invitation">
              <Stack gap="sm">
                <Text>
                  This invitation is invalid, has expired, or has already been accepted.
                </Text>
                <Text size="sm" c="dimmed">
                  If you believe this is an error, please contact the organization administrator.
                </Text>
              </Stack>
            </Alert>
            <Button
              fullWidth
              mt="lg"
              variant="light"
              onClick={() => navigate(user ? '/dashboard' : '/login')}
            >
              {user ? 'Go to Dashboard' : 'Go to Login'}
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Show declined state
  if (declined) {
    return (
      <Box
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0b487c 0%, #1a6eb5 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
        }}
      >
        <Container size={500}>
          <Center mb="xl">
            <Image src="/logo-inverse.png" alt="TrustPuma" h={80} w="auto" />
          </Center>

          <Paper withBorder shadow="xl" p="xl" radius="md">
            <Alert icon={<IconX size={24} />} color="gray" title="Invitation Declined">
              <Text>You have declined the invitation.</Text>
            </Alert>
            <Text size="sm" c="dimmed" mt="md" ta="center">
              Redirecting you...
            </Text>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Show success state (after accepting)
  if (acceptInvite.isSuccess) {
    return (
      <Box
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0b487c 0%, #1a6eb5 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
        }}
      >
        <Container size={500}>
          <Center mb="xl">
            <Image src="/logo-inverse.png" alt="TrustPuma" h={80} w="auto" />
          </Center>

          <Paper withBorder shadow="xl" p="xl" radius="md">
            <Alert icon={<IconCheck size={24} />} color="green" title="Welcome!">
              <Text>
                You have successfully joined{' '}
                <strong>{invite.organization?.name || 'the organization'}</strong>!
              </Text>
            </Alert>
            <Text size="sm" c="dimmed" mt="md" ta="center">
              Redirecting you to the dashboard...
            </Text>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Main invitation view
  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <Container size={500}>
        <Center mb="xl">
          <Image src="/logo-inverse.png" alt="TrustPuma" h={80} w="auto" />
        </Center>

        <Paper withBorder shadow="xl" p="xl" radius="md">
          <Stack gap="md">
            <div>
              <IconMail size={48} color="#0b487c" />
              <Title order={2} mt="md">
                You've Been Invited!
              </Title>
              <Text c="dimmed" size="sm" mt="xs">
                You have been invited to join an organization
              </Text>
            </div>

            <Card withBorder p="md" radius="md" bg="gray.0">
              <Stack gap="sm">
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Organization
                  </Text>
                  <Text size="lg" fw={600}>
                    {invite.organization?.name || 'Unknown Organization'}
                  </Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Role
                  </Text>
                  <Badge
                    size="lg"
                    color={invite.is_admin ? 'red' : customRole ? 'blue' : 'gray'}
                    variant="light"
                  >
                    {invite.is_admin ? 'Admin' : customRole?.name || 'Member'}
                  </Badge>
                </div>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Invited Email
                  </Text>
                  <Text size="sm">{invite.email}</Text>
                </div>
              </Stack>
            </Card>

            {acceptInvite.isError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red">
                {acceptInvite.error instanceof Error
                  ? acceptInvite.error.message
                  : 'Failed to accept invitation. Please try again.'}
              </Alert>
            )}

            {needsLogin && (
              <Alert icon={<IconAlertCircle size={16} />} color="blue">
                An account already exists for <strong>{invite.email}</strong>. Please log in to
                accept this invitation.
              </Alert>
            )}

            {user && user.email?.toLowerCase() !== invite.email.toLowerCase() && (
              <Alert icon={<IconAlertCircle size={16} />} color="orange">
                You are logged in as <strong>{user.email}</strong>, but this invitation was sent
                to <strong>{invite.email}</strong>. Please log out and try again.
              </Alert>
            )}

            <Group grow mt="md">
              <Button
                variant="outline"
                color="gray"
                leftSection={<IconX size={16} />}
                onClick={handleDecline}
                disabled={acceptInvite.isPending}
              >
                Decline
              </Button>
              <Button
                leftSection={<IconCheck size={16} />}
                onClick={handleAccept}
                loading={acceptInvite.isPending}
                disabled={user && user.email?.toLowerCase() !== invite.email.toLowerCase()}
              >
                {user ? 'Accept Invitation' : needsLogin ? 'Go to Login' : 'Accept & Create Account'}
              </Button>
            </Group>

            {needsLogin && (
              <Button
                variant="subtle"
                fullWidth
                onClick={() => navigate(`/login?redirect=/accept-invite?token=${token}`)}
              >
                Go to Login
              </Button>
            )}
          </Stack>
        </Paper>

        {/* For local testing - show the invite link */}
        {import.meta.env.DEV && (
          <Paper withBorder p="md" mt="md" bg="dark.8">
            <Text size="xs" c="dimmed" mb="xs">
              Dev Only - Invite Link:
            </Text>
            <Text size="xs" c="blue" style={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
              {window.location.origin}/accept-invite?token={token}
            </Text>
          </Paper>
        )}
      </Container>

      {/* Password Creation Modal */}
      <Modal
        opened={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title="Create Your Account"
        size="md"
        centered
      >
        <form onSubmit={passwordForm.onSubmit(handleCreateAccountAndAccept)}>
          <Stack gap="md">
            <Alert icon={<IconLock size={16} />} color="blue">
              Create a password for your account. You'll use this to log in to{' '}
              <strong>{invite?.organization?.name}</strong>.
            </Alert>

            <Text size="sm" c="dimmed">
              Creating account for: <strong>{invite?.email}</strong>
            </Text>

            <PasswordInput
              label="Password"
              placeholder="Enter your password"
              required
              {...passwordForm.getInputProps('password')}
            />

            <PasswordInput
              label="Confirm Password"
              placeholder="Confirm your password"
              required
              {...passwordForm.getInputProps('confirmPassword')}
            />

            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                onClick={() => setShowPasswordModal(false)}
                disabled={isCreatingAccount}
              >
                Cancel
              </Button>
              <Button type="submit" loading={isCreatingAccount} leftSection={<IconCheck size={16} />}>
                Create Account & Join
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Box>
  );
}
